import { useQuery } from "@tanstack/react-query";
import { Braces } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CopyContext, listCopyTags } from "../services/copy-tags-api";

// "Insert" button listing only the tags that work here — the admin never types
// a tag by hand. With a linked pack, each named session gets its own entry,
// e.g. "Reschedule fee — Individual (45 min)" → {{RESCHEDULE_PRICE:individual}}.
export default function InsertTagMenu({
  context,
  onInsert,
  disabled,
}: {
  context: CopyContext;
  onInsert: (tag: string) => void;
  disabled?: boolean;
}) {
  const { data, isLoading } = useQuery({
    queryKey: [
      "copy-tags",
      context.surface,
      context.bundleStandardId ?? null,
      context.regionId ?? null,
      context.productId ?? null,
    ],
    queryFn: () => listCopyTags(context),
    staleTime: 1000 * 60 * 5,
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={disabled}
        >
          <Braces className="h-3.5 w-3.5 mr-1" /> Insert price
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80"
        // Don't hand focus back to the trigger on close — the field takes it,
        // and the jump would scroll the page.
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel className="text-xs">
          Filled in per customer, at their own price
        </DropdownMenuLabel>
        {isLoading && (
          <DropdownMenuItem disabled className="text-xs">
            Loading…
          </DropdownMenuItem>
        )}
        {data?.sessions.map((s) => (
          <DropdownMenuItem
            key={s.insert}
            disabled={!s.hasFee}
            onSelect={() => onInsert(s.insert)}
          >
            <TagRow
              label={s.label}
              insert={s.hasFee ? s.insert : "No reschedule fee set up for this session"}
            />
          </DropdownMenuItem>
        ))}
        {!!data?.sessions.length && <DropdownMenuSeparator />}
        {data?.tags.map((t) => (
          <DropdownMenuItem key={t.insert} onSelect={() => onInsert(t.insert)}>
            <TagRow label={t.label} insert={t.insert} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TagRow({ label, insert }: { label: string; insert: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-sm">{label}</span>
      <code className="text-[11px] text-gray-500">{insert}</code>
    </div>
  );
}
